import { Reducer, useCallback, useEffect, useReducer } from "react"

import { defaultVideoElementState, VideoElementState } from "./VideoState"

function definePropIfNotDefined<Obj extends object>(
  target: Obj,
  propKey: string,
  attributes: PropertyDescriptor,
) {
  if (propKey in target) {
    return target
  }

  return Object.defineProperty(target, propKey, attributes)
}

const videoElementEventTypes = [
  "abort",
  "canplay",
  "canplaythrough",
  "durationchange",
  "emptied",
  "ended",
  "error",
  "loadeddata",
  "loadedmetadata",
  "loadstart",
  "pause",
  "play",
  "playing",
  "progress",
  "ratechange",
  "seeked",
  "seeking",
  "stalled",
  "suspend",
  "timeupdate",
  "volumechange",
  "waiting",
] as const

type VideoElementEventType = (typeof videoElementEventTypes)[number]

type VideoElementAction =
  | {
      type: VideoElementEventType
      payload: HTMLVideoElement
    }
  | {
      type: "SYNC"
      payload?: HTMLVideoElement | null
    }
  | {
      type: "SET_PREV_NON_ZERO_VOLUME"
      payload: {
        volume: number
      }
    }

type VideoElementStateReducer = Reducer<
  VideoElementState,
  VideoElementAction
>

const videoElementStateReducer: VideoElementStateReducer = (
  state,
  { type, payload },
) => {
  // On slider changes, store the prev non-zero volume so that it can be restored
  // later if necessary, when unmuting.
  if (type === "SET_PREV_NON_ZERO_VOLUME") {
    return {
      ...state,
      prevNonZeroVolume: payload.volume,
    }
  }

  // If we are syncing state and we have no video element then we are returning
  // to default values. Either the video element has not been created yet or
  // the video element has been destroyed.
  if (!payload) {
    // type === SYNC
    return {
      ...defaultVideoElementState,
    }
  }

  // In all other cases, start by syncing all the relevant state with the current
  // HTMLVideoElement state. This is less error prone than trying to determine
  // which properties can change in response to which events. Instead, just always
  // set these properties and there is no way for React to get out of sync with
  // the video. Later, in some special cases we may set other properties.
  const nextState: VideoElementState = {
    ...state,
    isPlaying: !payload.paused,
    videoTimestamp: payload.currentTime,
    isSeeking: payload.seeking,
    volume: payload.volume,
    isMuted: payload.muted,
  }

  if (type === "loadedmetadata") {
    nextState.canPlay = true
  }

  return nextState
}

export const useVideoEventListener = (
  videoElement: HTMLVideoElement | null,
) => {
  const [state, dispatch] = useReducer(
    videoElementStateReducer,
    defaultVideoElementState,
  )
  const onChange = useCallback(
    (event: Event) => {
      dispatch({
        type: event.type as VideoElementEventType,
        payload: event.currentTarget as HTMLVideoElement,
      })
    },
    [dispatch],
  )

  const setPrevNonZeroVolume = useCallback(
    (volume: number) => {
      dispatch({
        type: "SET_PREV_NON_ZERO_VOLUME",
        payload: { volume },
      })
    },
    [dispatch],
  )

  useEffect(() => {
    dispatch({
      type: "SYNC",
      payload: videoElement,
    })

    if (videoElement) {
      videoElementEventTypes.forEach((type) => {
        videoElement?.addEventListener(type, onChange)
      })
    }

    return () => {
      if (videoElement) {
        videoElementEventTypes.forEach((type) => {
          videoElement?.removeEventListener(type, onChange)
        })
      }
    }
  }, [videoElement, onChange])

  return definePropIfNotDefined(state, "setPrevNonZeroVolume", {
    value: setPrevNonZeroVolume,
  }) as VideoElementState & {
    setPrevNonZeroVolume: (volume: number) => void
  }
}
